/**
 * Hitbox helpers for the hamburger menu triggers (regular + institutional).
 *
 * Context: iOS/WKWebView requires the hamburger hit-test node to be a stable,
 * native button whose geometry exists on the first dashboard paint. The trigger
 * must therefore:
 *  - be at least 48x48 CSS px,
 *  - center its icon exactly inside that box,
 *  - never rely on negative offsets or transforms to look aligned.
 *
 * These helpers are pure so they can be unit tested, plus an opt-in runtime
 * diagnostic (?hitbox=1) that outlines button vs icon and logs measurements.
 */

export const MIN_TOUCH_TARGET_PX = 48;

export type Rect = { x: number; y: number; width: number; height: number };

export const centerOf = (rect: Rect) => ({
  x: rect.x + rect.width / 2,
  y: rect.y + rect.height / 2,
});

export type TransformChainEntry = {
  tag: string;
  id: string | null;
  transform: string;
  position: string;
  height: string;
};

type HitboxElements = {
  container?: HTMLElement | null;
  button?: HTMLElement | null;
};

type HitboxEventLike = {
  type: string;
  currentTarget: EventTarget | null;
  target: EventTarget | null;
  nativeEvent?: Event;
};

/** True when the icon's visual center falls inside the clickable button box. */
export function isVisualCenterWithinTarget(button: Rect, icon: Rect): boolean {
  const c = centerOf(icon);
  return (
    c.x >= button.x &&
    c.x <= button.x + button.width &&
    c.y >= button.y &&
    c.y <= button.y + button.height
  );
}

/** True when the button satisfies the 48x48 minimum touch target. */
export function meetsMinimumTouchTarget(button: Rect): boolean {
  return button.width >= MIN_TOUCH_TARGET_PX && button.height >= MIN_TOUCH_TARGET_PX;
}

/** Max distance (px) between button center and icon center. */
export function centerOffset(button: Rect, icon: Rect) {
  const b = centerOf(button);
  const i = centerOf(icon);
  return { dx: Math.round((i.x - b.x) * 100) / 100, dy: Math.round((i.y - b.y) * 100) / 100 };
}

function diagnosticsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).has("hitbox")) return true;
    return window.localStorage.getItem("mblearn:hitboxDebug") === "1";
  } catch {
    return false;
  }
}

function describeNode(node: EventTarget | Element | null | undefined): string | null {
  if (!node) return null;
  if (node === window) return "window";
  if (node === document) return "document";
  if (!(node instanceof Element)) return String(node);
  const id = node.id ? `#${node.id}` : "";
  const dataMenu = node.getAttribute("data-menu-trigger") || node.getAttribute("data-menu-touch-target");
  const data = dataMenu ? `[menu=${dataMenu}]` : "";
  const role = node.getAttribute("role") ? `[role=${node.getAttribute("role")}]` : "";
  return `${node.tagName.toLowerCase()}${id}${data}${role}`;
}

function rectPayload(el: Element | null | undefined) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    x: Math.round(r.x * 100) / 100,
    y: Math.round(r.y * 100) / 100,
    w: Math.round(r.width * 100) / 100,
    h: Math.round(r.height * 100) / 100,
    top: Math.round(r.top * 100) / 100,
    bottom: Math.round(r.bottom * 100) / 100,
  };
}

function layerPayload(el: Element | null | undefined) {
  if (!el || typeof window === "undefined") return null;
  const computed = window.getComputedStyle(el);
  return {
    node: describeNode(el),
    className: typeof (el as HTMLElement).className === "string" ? (el as HTMLElement).className : null,
    rect: rectPayload(el),
    position: computed.position,
    zIndex: computed.zIndex,
    pointerEvents: computed.pointerEvents,
    overflow: computed.overflow,
    overflowX: computed.overflowX,
    overflowY: computed.overflowY,
    opacity: computed.opacity,
    visibility: computed.visibility,
    transform: computed.transform,
  };
}

function layersFromPoint(x: number, y: number) {
  if (typeof document === "undefined" || !document.elementsFromPoint) return [];
  return document.elementsFromPoint(x, y).slice(0, 12).map(layerPayload).filter(Boolean);
}

function describeEventPath(event: Event | undefined) {
  if (!event || typeof event.composedPath !== "function") return [];
  return event.composedPath().slice(0, 16).map(describeNode);
}

function getClientPoint(event: HitboxEventLike) {
  const native = event.nativeEvent as (Event & {
    clientX?: number;
    clientY?: number;
    touches?: TouchList;
    changedTouches?: TouchList;
  }) | undefined;
  const touch = native?.touches?.[0] ?? native?.changedTouches?.[0];
  const x = touch?.clientX ?? native?.clientX;
  const y = touch?.clientY ?? native?.clientY;
  if (typeof x !== "number" || typeof y !== "number") return null;
  return { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
}

function inspectMenuTarget(button: HTMLElement | null, container: HTMLElement | null) {
  const svg = button?.querySelector("svg") ?? null;
  const containerRect = rectPayload(container);
  const buttonRect = rectPayload(button);
  const svgRect = rectPayload(svg);
  const svgBox = svg?.getBoundingClientRect();
  const center = svgBox ? centerOf(svgBox) : button ? centerOf(button.getBoundingClientRect()) : null;
  const centerPoint = center ? { x: Math.round(center.x * 100) / 100, y: Math.round(center.y * 100) / 100 } : null;
  const belowPoint = centerPoint ? { x: centerPoint.x, y: centerPoint.y + 12 } : null;

  return {
    rects: {
      container: containerRect,
      button: buttonRect,
      svg: svgRect,
    },
    points: {
      svgCenter: centerPoint,
      twelvePxBelowSvgCenter: belowPoint,
    },
    elementFromPoint: {
      svgCenter: centerPoint ? describeNode(document.elementFromPoint(centerPoint.x, centerPoint.y)) : null,
      twelvePxBelowSvgCenter: belowPoint ? describeNode(document.elementFromPoint(belowPoint.x, belowPoint.y)) : null,
    },
    layersAtSvgCenter: centerPoint ? layersFromPoint(centerPoint.x, centerPoint.y) : [],
    layersAtTwelvePxBelow: belowPoint ? layersFromPoint(belowPoint.x, belowPoint.y) : [],
    transformChain: getTransformChain(container ?? button),
  };
}

function describeElementForChain(el: Element): string {
  return `${el.tagName.toLowerCase()}${el.id ? `#${el.id}` : ""}`;
}

export function getTransformChain(from: HTMLElement | null): TransformChainEntry[] {
  if (typeof window === "undefined" || !from) return [];
  const chain: TransformChainEntry[] = [];
  let current: HTMLElement | null = from;
  while (current) {
    const computed = window.getComputedStyle(current);
    chain.push({
      tag: describeElementForChain(current),
      id: current.id || null,
      transform: computed.transform,
      position: computed.position,
      height: computed.height,
    });
    if (current === document.body) break;
    current = current.parentElement;
  }
  return chain;
}

function drawHitboxMarker(x: number, y: number, label: string, classSuffix = "") {
  const markerId = `menu-hitbox-marker-${label.replace(/[^a-z0-9_-]/gi, "-")}${classSuffix}`;
  const existing = document.getElementById(markerId);
  existing?.remove();
  const marker = document.createElement("div");
  marker.id = markerId;
  marker.setAttribute("aria-hidden", "true");
  marker.style.position = "fixed";
  marker.style.left = `${x}px`;
  marker.style.top = `${y}px`;
  marker.style.width = "10px";
  marker.style.height = "10px";
  marker.style.marginLeft = "-5px";
  marker.style.marginTop = "-5px";
  marker.style.border = classSuffix ? "2px solid orange" : "2px solid red";
  marker.style.borderRadius = "9999px";
  marker.style.background = "transparent";
  marker.style.pointerEvents = "none";
  marker.style.zIndex = "2147483647";
  document.body.appendChild(marker);
}

/**
 * Opt-in diagnostic: outlines the trigger and its icon and logs
 * button rect / svg rect / elementFromPoint at the icon's visual center.
 * No-op unless `?hitbox=1` (or localStorage flag) is present.
 */
export function measureTriggerHitbox(trigger: HTMLElement | null, label: string, container?: HTMLElement | null) {
  if (!trigger || !diagnosticsEnabled()) return;
  const touchContainer = container ?? trigger;
  const svg = trigger.querySelector("svg");
  const b = trigger.getBoundingClientRect();
  const s = svg?.getBoundingClientRect();
  touchContainer.style.outline = "2px solid rgba(255,0,0,.95)";
  trigger.style.outline = "1px solid rgba(255,140,0,.9)";
  if (svg) (svg as unknown as HTMLElement).style.outline = "1px solid rgba(0,128,255,.9)";
  const center = s ? centerOf(s) : centerOf(b);
  const below = { x: center.x, y: center.y + 12 };
  const hit = document.elementFromPoint(center.x, center.y);
  const belowHit = document.elementFromPoint(below.x, below.y);
  const transformChain = getTransformChain(touchContainer);
  const transformedAncestors = transformChain.filter((entry) => entry.transform && entry.transform !== "none");
  drawHitboxMarker(center.x, center.y, label);
  drawHitboxMarker(below.x, below.y, label, "-below-12px");
  const payload = {
    container: rectPayload(touchContainer),
    button: { x: b.x, y: b.y, w: b.width, h: b.height },
    svg: s ? { x: s.x, y: s.y, w: s.width, h: s.height } : null,
    center,
    below12: below,
    meets48: meetsMinimumTouchTarget(b),
    meets56Container: touchContainer.getBoundingClientRect().width >= 56 && touchContainer.getBoundingClientRect().height >= 56,
    centerInside: s ? isVisualCenterWithinTarget(b, s) : null,
    offset: s ? centerOffset(b, s) : null,
    elementFromPoint: hit
      ? {
          tag: hit.tagName.toLowerCase(),
          insideTrigger: trigger.contains(hit),
          menuTrigger: hit.closest("[data-menu-trigger]")?.getAttribute("data-menu-trigger") ?? null,
        }
      : null,
    elementFromPointBelow12: belowHit
      ? {
          tag: belowHit.tagName.toLowerCase(),
          insideContainer: touchContainer.contains(belowHit),
          insideTrigger: trigger.contains(belowHit),
          menuTrigger: belowHit.closest("[data-menu-trigger]")?.getAttribute("data-menu-trigger") ?? null,
          menuTouchTarget: belowHit.closest("[data-menu-touch-target]")?.getAttribute("data-menu-touch-target") ?? null,
        }
      : null,
    layersAtCenter: layersFromPoint(center.x, center.y),
    layersAtBelow12: layersFromPoint(below.x, below.y),
    transformChain,
    transformedAncestors,
  };
  // eslint-disable-next-line no-console
  console.info(`🎯 [Hitbox] ${label}`, payload);
  if (transformedAncestors.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`🎯 [Hitbox] ${label}: transformed ancestor detected`, transformedAncestors);
  }
}

export function logMenuTouchEvent(event: HitboxEventLike, label: string, elements: HitboxElements) {
  if (!diagnosticsEnabled()) return;
  const point = getClientPoint(event);
  if (point) drawHitboxMarker(point.x, point.y, label, "-touch");

  const native = event.nativeEvent;
  const receiver = point ? document.elementFromPoint(point.x, point.y) : null;
  const touchLayers = point ? layersFromPoint(point.x, point.y) : [];
  const payload = {
    eventType: event.type,
    client: point,
    currentTarget: describeNode(event.currentTarget),
    target: describeNode(event.target),
    nativeTarget: describeNode(native?.target),
    composedPath: describeEventPath(native),
    elementFromTouchPoint: describeNode(receiver),
    receiverInsideContainer: receiver ? Boolean(elements.container?.contains(receiver)) : null,
    receiverInsideButton: receiver ? Boolean(elements.button?.contains(receiver)) : null,
    touchLayers,
    menuTarget: inspectMenuTarget(elements.button ?? null, elements.container ?? null),
  };
  // eslint-disable-next-line no-console
  console.info(`🎯 [MenuTouch] ${label}`, payload);
}
